import { getStore } from "@/core/api/graphql";
import { convertBrlCartsToUsd } from "@/core/api/graphql/cart/mutations/mergeCart/convertBrlCartsToUsd";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { IS_DEVELOPMENT } from "@/core/constants";
import { globals } from "@/core/globals";
import { extractHostname, Logger } from "@/core/utilities";
import { BRL_CURRENCY_CODE, isBrlCurrencyDisabled } from "@/core/utilities/brlCurrency";
import type { StoreResponseType } from "@/core/api/graphql/types";

/**
 * A tab opened before `DisableBrlCurrency` was enabled still runs on BRL. When such a tab becomes visible again,
 * ask the server whether BRL is now disabled; if so, convert the user's (or guest's) BRL carts to USD and reload so
 * the page starts on USD with the converted items. Only tabs that loaded with the flag off and are on BRL watch.
 */
export const useBrlCurrencyFlagWatcher = () => {
  const { themeContext } = useThemeContext();
  let isChecking = false;

  const convertBrlCarts = async (): Promise<void> => {
    if (!globals.userId || !globals.storeId) {
      return;
    }

    await convertBrlCartsToUsd({
      storeId: globals.storeId,
      userId: globals.userId,
      cultureName: globals.cultureName,
    });
  };

  const reloadWhenBrlIsDisabled = async (): Promise<void> => {
    if (isChecking || document.visibilityState !== "visible" || globals.currencyCode !== BRL_CURRENCY_CODE) {
      return;
    }

    isChecking = true;

    try {
      const store = (await getStore(
        IS_DEVELOPMENT ? extractHostname(import.meta.env.APP_BACKEND_URL as string) : location.hostname,
      )) as StoreResponseType;

      if (store && isBrlCurrencyDisabled(store.settings.modules)) {
        await convertBrlCarts();
        location.reload();
      }
    } catch (error) {
      Logger.error("Failed to check whether BRL currency is disabled", error);
    } finally {
      isChecking = false;
    }
  };

  const handleVisibilityChange = (): void => {
    void reloadWhenBrlIsDisabled();
  };

  const watchBrlCurrencyFlag = (): (() => void) => {
    // The code should run for stale browser tabs that loaded with BRL enabled but might now have it disabled
    if (isBrlCurrencyDisabled(themeContext.value.storeSettings.modules)) {
      return () => undefined;
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  };

  return { watchBrlCurrencyFlag };
};
